import { Link } from 'react-router-dom';
import Navbar2 from '@/component/navbar2';
import Footer from '@/components/BrandFooter';
import L7Reveal from '@/components/landing/L7Reveal';
import { HumanBand } from '@/components/landing/L7Sections';
import { ArrowRight } from 'lucide-react';
import {
  AbAdjust, AbAegis, AbAim, AbBars, AbBearing, AbBoard, AbCore, AbDoc,
  AbExchange, AbFacet, AbStack, AbTag, type AbIcon,
} from '@/components/landing/Icons3D';
import '@/styles/landingL7.css';

/* Consulting service pages — Financial & Strategic Advisory, and AI & Technology.
   ---------------------------------------------------------------------------
   Both reference mockups are the same page with different content: a practice
   list (heading + deliverables strip + service cards), a packaged-engagements
   grid, the human band, a "what sits with specialists" boundaries block, and a
   CTA. So this is one renderer driven by two datasets rather than two files that
   drift apart.

   Consulting type is Montserrat with Fraunces italic accents (`l7-serif`) — the
   `.spx` mockups, not the Poppins/Inter of the accounting side. `.brand-consulting`
   in landingL7.css handles that and the blue palette. */

type Service = { title: string; body: string };
type Practice = {
  icon: AbIcon;
  tint: string;
  kicker: string;
  title: string;
  intro: string;
  deliverables: string;
  services: Service[];
};
type Engagement = { title: string; body: string; tag: string };

type PageData = {
  eyebrow: string;
  h1: [string, string];
  lead: string;
  cta: string;
  quicknav: string[];
  practices: Practice[];
  engagements: Engagement[];
  boundaries: { title: string; body: string }[];
  human: { title: string; lead: string; adviser: string; role: string; feats: string[]; chat: { from: 'them' | 'you'; text: string }[] };
  close: { title: string; body: string };
};

/* ── Financial & Strategic Advisory ─────────────────────────────────────── */
const FINANCIAL: PageData = {
  eyebrow: 'Financial & Strategic Advisory · Services',
  h1: ['Every financial decision, handled by a', 'senior advisor.'],
  lead: 'Strategy, modeling, valuation, transactions and the investor story, standalone or as one mandate. We sell outcomes, not hours.',
  cta: 'Talk to an advisor',
  quicknav: ['Strategy', 'Business planning', 'Modeling', 'Valuation & capital', 'Pricing', 'Transactions', 'Storytelling'],
  practices: [
    {
      icon: AbAim, tint: '#2E6DB4', kicker: 'Practice 01', title: 'Strategy Advisory',
      intro: 'Define the strategic choices, growth pathways, business model and financial implications required to create long-term value.',
      deliverables: 'Strategy report · options matrix · feasibility · strategic roadmap',
      services: [
        { title: 'Business strategy', body: 'Corporate/business-unit strategy, priorities, competitive positioning and roadmap.' },
        { title: 'Growth strategy', body: 'Growth pathways, expansion choices, new revenue opportunities and scenarios.' },
        { title: 'Market entry', body: 'Market attractiveness, entry options, economics, risk and sequencing.' },
        { title: 'Business model design', body: 'Value-creation logic, revenue & cost structure, monetization and alternatives.' },
        { title: 'Strategic planning', body: 'Multi-year plan, objectives, financial implications, milestones and decision agenda.' },
        { title: 'Feasibility study', body: 'Commercial & financial feasibility, investment need, break-even and risk.' },
      ],
    },
    {
      icon: AbDoc, tint: '#7A4FC0', kicker: 'Practice 02', title: 'Business Planning',
      intro: 'Convert a business concept or growth strategy into a rigorous, decision-ready business plan supported by financial analysis.',
      deliverables: 'A full written business plan, print- and email-ready',
      services: [
        { title: 'Full business plan', body: 'Narrative, market, model, strategy, financial plan, funding need and risks.' },
        { title: 'Investor business plan', body: 'Investor-oriented case linking opportunity, economics, capital and returns.' },
        { title: 'Lender business plan', body: 'Debt-oriented plan: cash generation, repayment capacity, downside protection.' },
        { title: 'Expansion / new venture', body: 'Business case for a new geography, product line, subsidiary or unit.' },
        { title: 'Management plan', body: 'Internal plan aligning priorities, targets, financial needs and milestones.' },
      ],
    },
    {
      icon: AbBars, tint: '#0E9AA7', kicker: 'Practice 03', title: 'Financial Planning & Modeling',
      intro: 'Build transparent, driver-based models that quantify growth, profitability, cash requirements, scenarios and returns.',
      deliverables: 'Integrated model · assumptions log · scenarios',
      services: [
        { title: 'Integrated financial model', body: 'Driver-based P&L, balance sheet and cash flow with linked assumptions.' },
        { title: 'Revenue forecast', body: 'Revenue build by customer, product, geography, channel, volume or price.' },
        { title: 'Cost & margin model', body: 'COGS, gross and contribution margin, EBITDA and break-even.' },
        { title: 'Unit economics', body: 'Customer/product economics, contribution margin, payback and break-even.' },
        { title: 'Cash flow & runway', body: 'Liquidity forecast, burn, runway, funding gaps and downside cases.' },
        { title: 'Scenario & sensitivity', body: 'Base/upside/downside and sensitivity to the assumptions that get challenged.' },
      ],
    },
    {
      icon: AbFacet, tint: '#E0912F', kicker: 'Practice 04', title: 'Valuation & Capital Advisory',
      intro: 'Assess enterprise value, financing options, ownership dilution, capital requirements and stakeholder economics.',
      deliverables: 'Valuation memo · cap table · funding roadmap',
      services: [
        { title: 'Company valuation', body: 'DCF, comparables, precedent transactions and venture methods, triangulated.' },
        { title: 'Pre-/post-money analysis', body: 'Round valuation, ownership implications, dilution and investor economics.' },
        { title: 'Cap table modeling', body: 'Current and pro-forma ownership, rounds, option pools, dilution and exit.' },
        { title: 'Capital requirements', body: 'Funding need, use of funds, runway, financing stages and timing.' },
        { title: 'Capital structure strategy', body: 'Debt/equity mix, leverage, financing scenarios and return trade-offs.' },
        { title: 'Investor returns', body: 'IRR, MOIC, cash-on-cash, exit values and waterfall scenarios.' },
      ],
    },
    {
      icon: AbTag, tint: '#F0776B', kicker: 'Practice 05', title: 'Pricing & Revenue Strategy',
      intro: 'Design revenue and pricing models that align customer value, growth, margin and scalability.',
      deliverables: 'Pricing model · revenue architecture · recommendations',
      services: [
        { title: 'Pricing strategy', body: 'Pricing logic, value capture, price architecture and willingness-to-pay.' },
        { title: 'Revenue model', body: 'Streams, pricing units, recurring/non-recurring mix and monetization logic.' },
        { title: 'Pricing architecture', body: 'Tiers, packages, plans, fees, discounts, minimums and add-ons.' },
        { title: 'Monetization strategy', body: 'How value creation converts into revenue and margin across segments.' },
        { title: 'Pricing economics', body: 'Price-volume-margin trade-offs, break-even impact and sensitivity.' },
        { title: 'Revenue model review', body: 'Scalability, concentration, predictability, margin and revenue risk.' },
      ],
    },
    {
      icon: AbExchange, tint: '#E85C9A', kicker: 'Practice 06', title: 'Transaction & Investment Advisory',
      intro: 'Test the commercial and financial case behind investments, acquisitions, financing events and exits.',
      deliverables: 'Diligence report · deal model · returns & risk',
      services: [
        { title: 'Commercial due diligence', body: 'Market, competition, business model, growth assumptions and commercial risk.' },
        { title: 'Financial due diligence', body: 'Revenue quality, margins, cash flow and key financial risks and adjustments.' },
        { title: 'Buy-side analysis', body: 'Target assessment, valuation, investment case, scenarios and deal economics.' },
        { title: 'Sell-side preparation', body: 'Model readiness, investment narrative, valuation support and materials.' },
        { title: 'M&A / acquisition model', body: 'Purchase price, financing, pro-forma economics, returns and sensitivities.' },
        { title: 'Exit analysis', body: 'Exit scenarios, valuation range, stakeholder proceeds and investor returns.' },
      ],
    },
    {
      icon: AbBoard, tint: '#5B8DEF', kicker: 'Practice 07', title: 'Storytelling & Executive Communications',
      intro: 'Turn strategy and financial analysis into clear, persuasive executive narratives for investors, boards and clients.',
      deliverables: 'Investor deck · two-pager · board / IC pack',
      services: [
        { title: 'Investor pitch deck', body: 'Narrative, model, traction, funding ask and use of funds: numbers agree.' },
        { title: 'Board / management deck', body: 'Decision-oriented narrative: strategic choices, financial impact, risks, asks.' },
        { title: 'Investment / IC deck', body: 'Transaction overview, valuation, diligence, thesis, risks and recommendation.' },
        { title: 'Financial storytelling', body: 'Translate models into charts, key messages and a coherent narrative.' },
        { title: 'Business storytelling', body: 'Structure the story: context, opportunity, choice, economics, call to action.' },
        { title: 'Presentation redesign', body: 'Upgrade structure, message hierarchy, headlines and decision clarity.' },
      ],
    },
  ],
  engagements: [
    { title: 'Startup fundraising readiness', body: 'Financial model + valuation + cap table + dilution scenarios + investor pitch deck + use of funds.', tag: 'Most popular' },
    { title: 'Investor-ready business plan', body: 'Business plan + financial model + valuation context + funding requirement + investor narrative.', tag: 'Planning' },
    { title: 'Valuation & capital structure review', body: 'Valuation + financing alternatives + pre/post-money + cap table + stakeholder economics.', tag: 'Valuation' },
    { title: 'Pricing & revenue model strategy', body: 'Revenue architecture + pricing structure + unit economics + sensitivity + recommendations.', tag: 'Pricing' },
    { title: 'Investment / acquisition due diligence', body: 'Commercial + financial diligence + valuation + returns + risk assessment + recommendation.', tag: 'Transactions' },
    { title: 'Board / investor decision pack', body: 'Analysis + financial story + strategic options + recommendation + executive presentation.', tag: 'Storytelling' },
  ],
  boundaries: [
    { title: 'Operations', body: 'No operational implementation, process-redesign execution or outsourced management.' },
    { title: 'Technology', body: 'Software builds, integration and AI engineering sit with our AI & Technology practice.' },
    { title: 'Marketing execution', body: 'No campaign management, SEO, media buying or creative production.' },
    { title: 'Legal / tax / audit', body: 'Analysis informs decisions; regulated opinions stay with qualified professionals.' },
  ],
  human: {
    title: 'A senior advisor on your side, not a junior with a template.',
    lead: 'Every mandate is led by an operator who has actually raised, valued and sold businesses. Real judgment on the calls that matter, not a deck pulled from a database.',
    adviser: 'Karan', role: 'your advisor',
    feats: ['Named senior advisor', 'Investor-grade judgment', 'On your call in 24h', 'Fixed fee, never a % of your raise'],
    chat: [
      { from: 'them', text: 'Took a first look at your model: the CAC payback is the number an investor will push on. Want me to build the sensitivity before your meeting?' },
      { from: 'you', text: 'Yes please' },
      { from: 'them', text: 'Done: base, upside and downside are in, and the story now matches the numbers.' },
    ],
  },
  close: {
    title: 'Turn a complex decision into an investable case.',
    body: "Tell us the decision you're working toward. We'll suggest the right scope and put a senior advisor on your call within 24 hours.",
  },
};

/* ── AI & Technology Advisory ───────────────────────────────────────────── */
const AI: PageData = {
  eyebrow: 'AI & Technology Advisory · Services',
  h1: ['AI, from raw idea to', 'production-grade.'],
  lead: 'Readiness, agent builds, product advisory and safe, production-grade code, standalone or combined. We ship working systems, not hours.',
  cta: 'Talk to an engineer',
  quicknav: ['Readiness & data', 'Agent build', 'Product advisory', 'MVP & code quality', 'Safety & governance'],
  practices: [
    {
      icon: AbStack, tint: '#2E6DB4', kicker: 'Practice 01', title: 'AI Process Mapping & Data Readiness',
      intro: 'Before building anything, understand how the business works and whether the data is usable, then map where AI actually creates value.',
      deliverables: 'Workflow map · data-readiness review · AI opportunity map · roadmap',
      services: [
        { title: 'Workflow mapping', body: 'Interview the team, map current workflows and document business processes clearly.' },
        { title: 'Data readiness review', body: 'Assess whether the data is usable; clean and structure business knowledge.' },
        { title: 'Knowledge structuring', body: 'Organise knowledge bases, databases or knowledge graphs; prepare the data layer.' },
        { title: 'AI opportunity mapping', body: 'Identify where AI or automation helps; prioritise the highest-value use cases.' },
        { title: 'AI roadmap', body: 'A clear implementation plan, with an optional first prototype or automation.' },
      ],
    },
    {
      icon: AbCore, tint: '#7A4FC0', kicker: 'Practice 02', title: 'AI Agent Build & Product Integration',
      intro: 'Design, code and integrate the AI system, working directly with your development team, with safety built in.',
      deliverables: 'Working AI agent · API integration · evaluation setup · handover',
      services: [
        { title: 'AI agent architecture', body: 'Design the agent architecture and choose the right stack: cloud, open source or private.' },
        { title: 'Agent logic & development', body: 'Build the agent logic and the core AI system.' },
        { title: 'Product / backend integration', body: 'Connect the agent to your product or backend, with your dev team.' },
        { title: 'Safety & governance checks', body: 'Add safety, evaluation and data-governance checks into the system.' },
        { title: 'Maintainability & handover', body: 'Make the system maintainable after delivery, with a clean developer handover.' },
      ],
    },
    {
      icon: AbBearing, tint: '#0E9AA7', kicker: 'Practice 03', title: 'AI Product & Architecture Advisory',
      intro: 'Senior AI leadership without a full-time hire: decide what to build, what to avoid, and how to ship responsibly.',
      deliverables: 'AI product roadmap · architecture reviews · risk reviews',
      services: [
        { title: 'AI idea review & prioritisation', body: 'Review AI product ideas and prioritise the highest-value, feasible use cases.' },
        { title: 'System architecture', body: 'Define the system architecture and technical direction across initiatives.' },
        { title: 'Developer guidance', body: 'Guide internal developers and review key technical decisions.' },
        { title: 'Implementation planning', body: 'Support implementation planning, sequencing and delivery priorities.' },
        { title: 'Pre-launch risk review', body: 'Review risks and quality before launch: prototype to reliable system.' },
      ],
    },
    {
      icon: AbAdjust, tint: '#E0912F', kicker: 'Practice 04', title: 'AI MVP & Codebase Cleanup',
      intro: 'The visible product works, but the hidden parts are weak. We make a quick AI-built demo ready for real users.',
      deliverables: 'Codebase review · risk & cleanup plan · test plan · clean structure',
      services: [
        { title: 'Codebase review', body: 'Review the codebase; identify security, backend, data and quality risks.' },
        { title: 'Backend / frontend separation', body: 'Separate responsibilities and define clean API endpoints.' },
        { title: 'Security & data review', body: 'Harden security; fix inconsistent data models and hardcoded data.' },
        { title: 'Testing & QA', body: 'Add unit and integration tests, with a clear test plan.' },
        { title: 'Deployment & debugging', body: 'Improve deployment and debugging structure for reliable releases.' },
        { title: 'Production readiness', body: 'Prepare the product for real users, with refactoring priorities.' },
      ],
    },
    {
      icon: AbAegis, tint: '#15824E', kicker: 'Practice 05', title: 'AI Safety, Evaluation & Governance',
      intro: 'AI systems need clear data boundaries, safe backend design and proper testing before they meet real clients.',
      deliverables: 'Data-governance setup · evaluation harness · senior review',
      services: [
        { title: 'Data boundaries & governance', body: 'Establish clear data boundaries, privacy handling and safe governance.' },
        { title: 'Evaluation setup', body: 'Set up testing and evaluation for AI outputs, quality and reliability.' },
        { title: 'Deployment strategy', body: 'Cloud, open-source or private: the right trade-off for privacy and cost.' },
        { title: 'Senior technical review', body: 'Senior review of high-stakes architecture: safe backend and testing.' },
      ],
    },
  ],
  engagements: [
    { title: 'AI readiness & data assessment', body: 'Workflow map + data-readiness review + AI opportunity map + implementation roadmap.', tag: 'Most popular' },
    { title: 'AI agent build', body: 'Agent architecture + build + product/backend integration + safety checks + handover.', tag: 'Build' },
    { title: 'AI MVP hardening', body: 'Codebase review + security/backend cleanup + API design + tests + deployment + production readiness.', tag: 'Quality' },
    { title: 'Fractional AI product advisory', body: 'Ongoing idea review + architecture + developer guidance + pre-launch risk reviews.', tag: 'Advisory' },
    { title: 'AI proof-of-concept', body: 'A focused first prototype or automation to prove value before a full build.', tag: 'Prototype' },
    { title: 'Pre-launch technical review', body: 'Architecture, security, evaluation and quality review before go-live.', tag: 'Review' },
  ],
  boundaries: [
    { title: 'Business & financial', body: 'Strategy, financial modeling, valuation and fundraising sit with our Financial Advisory practice.' },
    { title: 'Marketing execution', body: 'No campaign management, SEO, media buying or creative production.' },
    { title: 'Ongoing operations', body: 'Focused engagements or fractional advisory, not a permanent managed dev shop unless agreed.' },
    { title: 'Regulated domains', body: 'For healthcare or financial AI, clinical and legal sign-off remain with qualified professionals.' },
  ],
  human: {
    title: 'A senior engineer on your side, not a black box.',
    lead: 'Every build is led by a senior AI engineer or architect who ships production systems for a living. Real judgment on data boundaries, safety and architecture, not a prototype thrown over the wall.',
    adviser: 'Khaled', role: 'your AI lead',
    feats: ['Named senior engineer', 'Safety & data boundaries first', 'On your call in 24h', 'Production-ready, handed over'],
    chat: [
      { from: 'them', text: 'Looked at your MVP: the backend and frontend are tangled and there are no tests. I would separate them and add an evaluation harness before you onboard anyone.' },
      { from: 'you', text: 'Makes sense' },
      { from: 'them', text: 'Done: clean APIs, tests passing, data boundaries set. Safe to put in front of real users now.' },
    ],
  },
  close: {
    title: 'From AI idea to a system you can ship.',
    body: "Tell us where your AI is stuck. We'll suggest the right scope and put a senior engineer on your call within 24 hours.",
  },
};

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function ServicesPage({ data }: { data: PageData }) {
  return (
    <div className="l7">
      <L7Reveal />
      <Navbar2 />

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="l7-sec" style={{ background: 'radial-gradient(1000px 520px at 80% -20%, var(--acc-soft), transparent 60%), var(--cream)' }}>
        <div className="l7-wrap">
          <span className="l7-eyebrow">{data.eyebrow}</span>
          <h1 style={{ margin: '18px 0 0', maxWidth: 780 }}>
            {data.h1[0]} <span className="l7-serif">{data.h1[1]}</span>
          </h1>
          <p className="l7-lead" style={{ margin: '18px 0 26px', maxWidth: 620 }}>{data.lead}</p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Link to="/contact" className="l7-btn l7-btn--acc">{data.cta} <ArrowRight size={16} /></Link>
            <a href="#engagements" className="l7-btn l7-btn--ghost">See packaged offers</a>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 32 }}>
            {data.practices.map((p, i) => (
              <a key={p.title} href={`#${slug(p.title)}`} style={{
                border: '1.5px solid var(--line)', background: '#fff', borderRadius: 999,
                padding: '8px 15px', fontSize: 12.5, fontWeight: 800, color: 'var(--ink2)', textDecoration: 'none',
              }}>{data.quicknav[i] ?? p.title}</a>
            ))}
          </div>
        </div>
      </section>

      {/* ── Practices ────────────────────────────────────────── */}
      {data.practices.map(({ icon: Icon, tint, kicker, title, intro, deliverables, services }, i) => (
        <section
          key={title}
          id={slug(title)}
          className={`l7-sec ${i % 2 ? 'l7-sec--paper' : ''}`}
          style={{ borderTop: '1px solid var(--line)' }}
        >
          <div className="l7-wrap" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,.82fr) minmax(0,1.18fr)', gap: 40 }}>
            <div>
              <span style={{ width: 46, height: 46, borderRadius: 13, display: 'grid', placeItems: 'center', background: `${tint}1f`, color: tint, marginBottom: 14 }}>
                <Icon size={23} strokeWidth={2} />
              </span>
              <span className="l7-serif" style={{ fontSize: 15, color: 'var(--acc-d)', display: 'block' }}>{kicker}</span>
              <h2 style={{ fontSize: 'clamp(24px,3vw,32px)', margin: '8px 0 12px' }}>{title}</h2>
              <p style={{ fontSize: 14.5, color: 'var(--ink2)', lineHeight: 1.6 }}>{intro}</p>
              <span style={{
                display: 'inline-block', marginTop: 16, background: 'var(--acc-soft)', border: '1px solid var(--line2)',
                color: 'var(--acc-d)', borderRadius: 12, padding: '10px 14px', fontSize: 12, fontWeight: 700, lineHeight: 1.5,
              }}>
                Deliverables: {deliverables}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 14, alignContent: 'start' }}>
              {services.map(s => (
                <div className="l7-card" key={s.title} style={{ padding: '16px 17px', borderRadius: 14 }}>
                  <b style={{ fontSize: 14, display: 'block', marginBottom: 5 }}>{s.title}</b>
                  <span style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>{s.body}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* ── Packaged engagements ─────────────────────────────── */}
      <section className="l7-sec l7-sec--paper2" id="engagements" style={{ borderTop: '1px solid var(--line)' }}>
        <div className="l7-wrap">
          <div style={{ maxWidth: 720, marginBottom: 34 }}>
            <span className="l7-eyebrow">Proposal-ready</span>
            <h2 style={{ margin: '12px 0 8px' }}>Packaged engagements.</h2>
            <p className="l7-lead">Ready-to-scope offers that combine the practices above: pick one, or we&rsquo;ll shape a custom mandate.</p>
          </div>
          <div className="l7-grid l7-grid--3">
            {data.engagements.map(e => (
              <div className="l7-card" key={e.title}>
                <h3 style={{ fontSize: 17, marginBottom: 8 }}>{e.title}</h3>
                <p style={{ fontSize: 13, lineHeight: 1.55 }}>{e.body}</p>
                <span style={{
                  display: 'inline-block', marginTop: 14, fontSize: 10.5, fontWeight: 800, letterSpacing: '.09em',
                  textTransform: 'uppercase', color: 'var(--acc-d)', background: 'var(--acc-soft)', borderRadius: 999, padding: '4px 10px',
                }}>{e.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HumanBand
        eyebrow="The human touch"
        title={data.human.title}
        lead={data.human.lead}
        adviser={data.human.adviser}
        role={data.human.role}
        chat={data.human.chat}
        feats={data.human.feats}
      />

      {/* ── What sits with specialists ───────────────────────── */}
      <section className="l7-sec" style={{ borderTop: '1px solid var(--line)' }}>
        <div className="l7-wrap">
          <div style={{ maxWidth: 720, marginBottom: 30 }}>
            <span className="l7-eyebrow">Clear scope</span>
            <h2 style={{ margin: '12px 0 8px' }}>What sits with specialists.</h2>
            <p className="l7-lead">We&rsquo;re explicit about what we don&rsquo;t claim: it protects your outcome and keeps the engagement clean.</p>
          </div>
          <div className="l7-grid l7-grid--2">
            {data.boundaries.map(b => (
              <div className="l7-card" key={b.title} style={{ padding: '16px 18px', borderRadius: 14 }}>
                <b style={{ fontSize: 13.5, display: 'block', marginBottom: 4 }}>{b.title}</b>
                <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>{b.body}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Close ────────────────────────────────────────────── */}
      <section className="l7-sec">
        <div className="l7-wrap">
          <div style={{ background: 'var(--acc)', borderRadius: 26, padding: 'clamp(32px,5vw,50px)', textAlign: 'center', color: '#fff' }}>
            <h2 style={{ color: '#fff' }}>{data.close.title}</h2>
            <p style={{ color: '#dbeafe', fontSize: 16, margin: '12px auto 24px', maxWidth: 540 }}>{data.close.body}</p>
            <Link to="/contact" className="l7-btn" style={{ background: '#fff', color: 'var(--acc-d)', borderColor: '#fff' }}>
              {data.cta} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export function FinancialAdvisory() { return <ServicesPage data={FINANCIAL} />; }
export default function AiTechnology() { return <ServicesPage data={AI} />; }
